import AppComponent from '../base/app-component.ts';
import type { DataTable, ColumnDef, RowAction } from './data-table.ts';
import { formatNumero } from '../lib/utils.ts';
import { raccogliDateFilters, handleDateOpChange, resetDateRanges } from '../lib/date-filter.ts';
import { getCasseMap } from '../lib/casse-info.ts';

const COLUMNS: ColumnDef[] = [
  { key: 'cassa', label: 'Cassa', type: 'text' },
  { key: 'codImp', label: 'Cod.', type: 'code', sortable: true },
  { key: 'ragioneSociale', label: 'Ragione Sociale', type: 'text', filterable: true, sortable: true },
  { key: 'codiceFiscale', label: 'Codice Fiscale', type: 'code', filterable: true },
  { key: 'partitaIva', label: 'Partita IVA', type: 'code' },
  { key: 'citta', label: 'Citta', type: 'text', sortable: true },
  { key: 'provincia', label: 'Prov.', type: 'text' },
];

const ACTIONS: RowAction[] = [
  { name: 'modifica-impresa', icon: 'pencil', btnClass: 'btn-outline-secondary py-0 px-1', title: 'Modifica', perm: 'amministratore,operatore' },
];

export class TabellaImprese extends AppComponent {
  static override componentName = 'wc-tabella-imprese';
  protected override get templateId() { return 'tabella-imprese'; }

  private dataTable: DataTable | null = null;
  private currentSort: string | null = null;
  private currentSortDir: 'asc' | 'desc' | null = null;
  private currentFilter = '';
  private currentPage = 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private offcanvas: any = null;
  private modals: { dispose(): void }[] = [];

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      columnsJson: JSON.stringify(COLUMNS),
      actionsJson: JSON.stringify(ACTIONS),
      casse: Object.values(getCasseMap()),
    };
  }

  protected override afterRender(): void {
    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;

    this.setupTableEvents();
    this.setupFiltriAvanzati();
    this.setupFiltriRapidi();
    this.setupEditImpresa();
    this.caricaImprese();
  }

  private setupTableEvents(): void {
    if (!this.dataTable) return;

    this.listen(this.dataTable, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page;
      this.caricaImprese();
    });

    this.listen(this.dataTable, 'dt-sort-change', (e: Event) => {
      const { sort, dir } = (e as CustomEvent).detail;
      this.currentSort = sort;
      this.currentSortDir = dir;
      this.currentPage = 1;
      this.caricaImprese();
    });

    this.listen(this.dataTable, 'dt-filter-change', (e: Event) => {
      this.currentFilter = (e as CustomEvent).detail.filter;
      this.currentPage = 1;
      this.caricaImprese();
    });

    this.listen(this.dataTable, 'dt-row-click', (e: Event) => {
      const { row } = (e as CustomEvent).detail;
      window.location.href = `/imprese/${row.id}`;
    });

    this.listen(this.dataTable, 'dt-row-action', (e: Event) => {
      const { action, row } = (e as CustomEvent).detail;
      if (action === 'modifica-impresa') {
        const id = Number(row.id);
        if (!id) return;
        const comp = document.querySelector('#modalEditImpresa wc-edit-impresa') as HTMLElement & { edit(id: number): void } | null;
        if (!comp) return;
        if (row.origine === 'MANUALE') comp.setAttribute('mode', 'manuale');
        else comp.removeAttribute('mode');
        comp.edit(id);
        this.getModal('#modalEditImpresa')?.show();
      }
    });
  }

  private setupFiltriAvanzati(): void {
    const offcanvasEl = this.$('[data-offcanvas="filtri"]');
    if (offcanvasEl) {
      this.offcanvas = new (globalThis as any).bootstrap.Offcanvas(offcanvasEl);
    }

    this.delegate.on('click', '[data-action="apri-filtri"]', () => {
      this.offcanvas?.show();
    });

    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.offcanvas?.hide();
        this.syncButtonGroup('cassa');
        this.currentPage = 1;
        this.caricaImprese();
      });
    }

    this.delegate.on('click', '[data-action="reset-filtri"], [data-action="reset-filtri-toolbar"]', () => {
      this.resetFiltri();
      this.offcanvas?.hide();
      this.currentPage = 1;
      this.caricaImprese();
    });

    this.delegate.on('change', '[data-date-op]', (e: Event) => handleDateOpChange(this, e));
  }

  private setupFiltriRapidi(): void {
    this.delegate.on('click', '[data-filtro-rapido] button', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('button') as HTMLElement;
      const group = btn.closest('[data-filtro-rapido]') as HTMLElement;
      const campo = group.dataset.filtroRapido!;
      const value = btn.dataset.value!;
      const wasActive = btn.classList.contains('active');

      group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      if (!wasActive) btn.classList.add('active');

      const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
      if (select) select.value = wasActive ? '' : value;

      this.currentPage = 1;
      this.caricaImprese();
    });
  }

  private syncButtonGroup(campo: string): void {
    const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
    const group = this.$(`[data-filtro-rapido="${campo}"]`);
    if (!select || !group) return;
    const val = select.value;
    group.querySelectorAll('button').forEach(b => {
      const btn = b as HTMLElement;
      btn.classList.toggle('active', btn.dataset.value === val);
    });
  }

  private raccogliFiltri(): Record<string, string> {
    const params: Record<string, string> = {};

    const testoFields = ['ragioneSociale', 'codiceFiscale', 'partitaIva', 'codImp', 'citta', 'provincia', 'indirizzo'];
    for (const campo of testoFields) {
      const val = (this.$(`[data-filtro-val="${campo}"]`) as HTMLInputElement | null)?.value?.trim();
      const op = (this.$(`[data-filtro-op="${campo}"]`) as HTMLSelectElement | null)?.value;
      if (val) {
        params[campo] = val;
        if (op) params[`${campo}Op`] = op;
      }
    }

    const cassaVal = (this.$('[data-filtro-val="cassa"]') as HTMLSelectElement | null)?.value;
    if (cassaVal) params.cassa = cassaVal;

    Object.assign(params, raccogliDateFilters(this, ['dataIscrizione', 'dataCessazione']));

    return params;
  }

  private resetFiltri(): void {
    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    form?.reset();
    resetDateRanges(this);
    this.syncButtonGroup('cassa');
  }

  private updateFiltriCount(): void {
    const filtri = this.raccogliFiltri();
    const count = Object.keys(filtri).filter(k => !k.endsWith('Op')).length;
    const badge = this.$('[data-filtri-count]');
    if (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.classList.toggle('d-none', count === 0);
    }
    const resetBtn = this.$('[data-action="reset-filtri-toolbar"]');
    resetBtn?.classList.toggle('d-none', count === 0);
  }

  private async caricaImprese(): Promise<void> {
    if (!this.dataTable) return;
    this.dataTable.setLoading();

    const filtri = this.raccogliFiltri();
    this.updateFiltriCount();

    const result = await this.api.imprese.lista({
      ...filtri,
      page: this.currentPage,
      limit: 30,
      sort: this.currentSort ?? undefined,
      sortDir: this.currentSortDir ?? undefined,
      q: this.currentFilter || undefined,
    });

    if (!result.ok) {
      this.dataTable.setError(result.error);
      return;
    }

    const { imprese, count, totalPages, pageSize, page } = result.data;
    this.dataTable.setData(imprese as unknown as Record<string, unknown>[], {
      page, totalPages, count, pageSize,
    });
    this.updateTotale(count);
  }

  private updateTotale(count: number): void {
    const el = document.querySelector('[data-totale-imprese]');
    if (el) el.textContent = formatNumero(count) + ' risultati';
  }

  // --- Edit Impresa ---

  private setupEditImpresa(): void {
    const modalEl = this.$('[data-modal="edit-impresa"]');
    if (modalEl) document.body.appendChild(modalEl);

    this.listen(document, 'impresa-salvata', ((e: CustomEvent) => {
      const target = e.target as Element | null;
      if (!target?.closest('#modalEditImpresa')) return;
      this.getModal('#modalEditImpresa')?.hide();
      this.currentPage = 1;
      this.caricaImprese();
    }) as EventListener);
  }

  private getModal(selector: string): { show(): void; hide(): void; dispose(): void } | null {
    const el = document.querySelector(selector);
    if (!el) return null;
    const bs = (globalThis as any).bootstrap;
    const existing = bs?.Modal?.getInstance(el);
    if (existing) return existing;
    const m = new bs.Modal(el);
    this.modals.push(m);
    return m;
  }

  protected override onDisconnected(): void {
    this.offcanvas?.dispose();
    for (const m of this.modals) {
      try { m.dispose(); } catch { /* ignore */ }
    }
    const modale = document.querySelector('[data-modal="edit-impresa"]');
    if (modale) modale.remove();
  }
}
